/**
 * @file useAppHotkeys — глобальные клавиатурные сокращения приложения
 * @description Использует Mantine useHotkeys для регистрации глобальных хоткеев.
 * Ctrl+S / Cmd+S — сохранить текущую форму
 * Ctrl+N — создать новый (открыть модалку)
 * Ctrl+F / "/" — фокус на поиск
 * Escape — закрыть активную модалку
 * @see docs/tz/01-phase-mvp.md §2.13 (Горячие клавиши)
 */
import { useHotkeys } from '@mantine/hooks';
import { useUIStore } from '@/stores/uiStore';

/**
 * Регистрирует глобальные хоткеи приложения.
 * Вызывается в AppLayout для регистрации на уровне layout.
 */
export function useAppHotkeys() {
  const closeModal = useUIStore((s) => s.closeModal);
  const activeModal = useUIStore((s) => s.activeModal);
  const togglePresentation = useUIStore((s) => s.togglePresentation);

  useHotkeys([
    // Ctrl+S / Cmd+S — submit активной формы
    [
      'mod+s',
      (event) => {
        event.preventDefault();

        // Найти ближайшую видимую форму и submit
        const activeElement = document.activeElement;
        const form = activeElement?.closest('form');
        if (form) {
          form.requestSubmit();
          return;
        }

        // Fallback: найти кнопку submit в видимых модальных окнах
        const modal = document.querySelector('.mantine-Modal-content');
        if (modal) {
          const submitBtn = modal.querySelector<HTMLButtonElement>(
            'button[type="submit"], button[data-save]',
          );
          if (submitBtn) {
            submitBtn.click();
            return;
          }
        }

        // Общий fallback: ищем любую видимую форму
        const forms = document.querySelectorAll('form');
        const lastForm = forms[forms.length - 1];
        if (lastForm) {
          lastForm.requestSubmit();
        }
      },
    ],

    // Ctrl+N — открыть модалку создания
    [
      'mod+n',
      (event) => {
        event.preventDefault();
        // Кликнуть кнопку "Создать" / "Добавить" на текущей странице
        const createBtn = document.querySelector<HTMLButtonElement>(
          'button[data-create], button[data-action="create"]',
        );
        if (createBtn) {
          createBtn.click();
        }
      },
    ],

    // Ctrl+F — фокус на поиск
    [
      'mod+f',
      (event) => {
        const searchInput = document.querySelector<HTMLInputElement>(
          'input[data-search], input[type="search"], input[placeholder*="Поиск"], input[placeholder*="поиск"]',
        );
        if (searchInput) {
          event.preventDefault();
          searchInput.focus();
          searchInput.select();
        }
        // Если нет search input — не перехватываем, пусть работает нативный Ctrl+F
      },
    ],

    // Ctrl+Shift+P — режим презентации
    [
      'mod+shift+p',
      (event) => {
        event.preventDefault();
        togglePresentation();
      },
    ],

    // Escape — закрыть модалку или выйти из презентации
    [
      'Escape',
      () => {
        if (activeModal) {
          closeModal();
        } else {
          const presentationMode = useUIStore.getState().presentationMode;
          if (presentationMode) {
            togglePresentation();
          }
        }
      },
    ],
  ]);

  // "/" — фокус на поиск (вне input/textarea)
  useHotkeys([
    [
      '/',
      (event) => {
        const tag = (event.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        const searchInput = document.querySelector<HTMLInputElement>(
          'input[data-search], input[type="search"], input[placeholder*="Поиск"], input[placeholder*="поиск"]',
        );
        if (searchInput) {
          event.preventDefault();
          searchInput.focus();
          searchInput.select();
        }
      },
    ],
  ]);
}
